import type { Board } from "../board";
import type { CollectableItem } from "../collectableItem";
import { LootPickupEffect, type LootPickupItem } from "../effects/lootPickupEffect";
import { PRIORITY_LAST, type ProcessableThing } from "../interfaces";
import { Ammo } from "../items/ammo";
import type { SimpleBoard } from "../levelgenerator/simpleBoard";
import { PhysicalThing } from "../physicalThing";
import { deserializeItem, deserializeXY, hasItem, hasWeaponClass } from "../serialization";
import type { XY } from "../xy";
import { ZOrder } from "../zOrder";
import { Sound } from "./sounds/sound";
import { SoundId } from "./sounds/soundId";

export class LootBox extends PhysicalThing implements ProcessableThing {
	static key = "LootBox";
	protected loot: CollectableItem[] = [];

	constructor(xy: XY, loot?: CollectableItem[]) {
		super(xy, false);
		if (loot) this.loot = loot;
	}

	static override initOnBoard(xy: XY, board: SimpleBoard, props?: Record<string, unknown>): PhysicalThing[] {
		const loot: CollectableItem[] = [];
		if (props?.lootKey) {
			const lootKey = props.lootKey as string;
			const count = (props.count as number) ?? 1;
			if (hasWeaponClass(lootKey)) {
				loot.push(new Ammo(lootKey, count));
			} else if (hasItem(lootKey)) {
				loot.push(deserializeItem({ type: lootKey, count }));
			} else {
				throw new Error(`LootBox: unknown lootKey '${lootKey}'`);
			}
		}
		const Ctor = this as unknown as { new (xy: XY, loot?: CollectableItem[]): LootBox };
		const box = new Ctor(xy, loot.length > 0 ? loot : undefined);
		board.putThing(box);
		return [box];
	}

	getZOrder(): number {
		return ZOrder.ITEM_ON_FLOOR;
	}

	addLoot(item: CollectableItem): void {
		this.loot.push(item);
	}

	getLootList(): CollectableItem[] {
		return this.loot;
	}

	processTurn(board: Board): void {
		if (!board.player || !board.player.xy.equals(this.xy)) return;

		const pickupItems: LootPickupItem[] = [];

		for (const item of this.loot) {
			const result = item.pickupBy(board.player, board);
			pickupItems.push({
				pickupKey: result.pickupKey,
				amount: result.amount,
				isInfinite: result.isInfinite,
			});
		}

		board.addEffect(new LootPickupEffect(this.xy, pickupItems));
		board.addNonPhysicalThing(new Sound(SoundId.PICKUP_BOX));
		board.removeAtEndOfTurn(this);
	}

	getProcessPriority(): number {
		return PRIORITY_LAST;
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			loot: this.loot.map((item) => item.serialize()),
		};
	}

	static deserialize(data: any): LootBox {
		const box = new LootBox(deserializeXY(data.xy));
		box.visualId = data.visualId;
		if (data.loot) {
			box.loot = data.loot.map((d: any) => deserializeItem(d));
		}
		return box;
	}
}
